// ═══════════════════════════════════════════════════════════════
// QuickAddPicker — the modal that opens when you tap "+ NEW" on
// Home. Three choice cards:
//   - Customer  → opens CustomerModal in place (handled by parent)
//   - Vehicle   → second step: pick a customer first → navigate
//                 to that customer's page with ?addVehicle=1, which
//                 the Jobs.jsx page reads to auto-open the Add
//                 Vehicle modal
//   - Appointment → navigates to /calendar?new=1
//
// Vehicles are always tied to a customer, so the picker collects
// the customer up front rather than dumping the user on a list and
// hoping they figure out the next step.
// ═══════════════════════════════════════════════════════════════
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, ArrowLeft, UserPlus, Car, CalendarPlus, Search,
} from 'lucide-react';

export default function QuickAddPicker({ customers, onClose, onPickCustomer }) {
  // 'choose' (default) → three big cards
  // 'pickCustomer'     → customer search list (only for Vehicle path)
  const [step, setStep] = useState('choose');
  const navigate = useNavigate();

  return (
    <div
      className="modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal-box leave-modal" role="dialog" aria-modal="true">
        {step === 'choose' && (
          <>
            <div className="modal-header">
              <h2>What are you adding?</h2>
              <button type="button" className="btn-link" onClick={onClose} aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div className="leave-choice-grid quickadd-grid">
              <button
                type="button"
                className="leave-choice"
                onClick={() => { onPickCustomer(); }}
              >
                <UserPlus size={28} />
                <span className="leave-choice-title">Customer</span>
                <span className="leave-choice-sub">Name, phone, email — the whole record</span>
              </button>
              <button
                type="button"
                className="leave-choice"
                onClick={() => setStep('pickCustomer')}
                disabled={customers.length === 0}
                title={customers.length === 0 ? 'Add a customer first' : ''}
              >
                <Car size={28} />
                <span className="leave-choice-title">Vehicle</span>
                <span className="leave-choice-sub">
                  {customers.length === 0 ? 'Add a customer first' : 'For an existing customer'}
                </span>
              </button>
              <button
                type="button"
                className="leave-choice"
                onClick={() => { onClose(); navigate('/calendar?new=1'); }}
              >
                <CalendarPlus size={28} />
                <span className="leave-choice-title">Appointment</span>
                <span className="leave-choice-sub">Schedule a job on the calendar</span>
              </button>
            </div>
          </>
        )}

        {step === 'pickCustomer' && (
          <PickCustomerStep
            customers={customers}
            onBack={() => setStep('choose')}
            onClose={onClose}
            onPick={(c) => {
              onClose();
              navigate(`/customers/${c.id}?addVehicle=1`);
            }}
          />
        )}
      </div>
    </div>
  );
}

// ─── Customer search list ───────────────────────────────────────
function PickCustomerStep({ customers, onBack, onClose, onPick }) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return customers;
    const termDigits = q.replace(/\D/g, '');
    return customers.filter((c) => {
      if (c.name?.toLowerCase().includes(term)) return true;
      if (termDigits && String(c.phone || '').replace(/\D/g, '').includes(termDigits)) return true;
      return false;
    });
  }, [customers, q]);

  return (
    <>
      <div className="modal-header">
        <button type="button" className="btn-link" onClick={onBack} aria-label="Back">
          <ArrowLeft size={18} />
        </button>
        <h2 style={{ flex: 1, textAlign: 'center', margin: 0 }}>Which customer?</h2>
        <button type="button" className="btn-link" onClick={onClose} aria-label="Close">
          <X size={18} />
        </button>
      </div>
      <div className="search-row" style={{ maxWidth: 'none', marginBottom: 12 }}>
        <Search size={16} />
        <input
          type="search"
          placeholder="Search by name or phone…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
        />
      </div>
      <ul className="quickadd-customer-list">
        {filtered.length === 0 ? (
          <li className="picker-empty">No customers match.</li>
        ) : (
          filtered.map((c) => (
            <li key={c.id}>
              <button type="button" className="quickadd-customer-row" onClick={() => onPick(c)}>
                <span className="quickadd-customer-name">{c.name}</span>
                {c.phone && <span className="muted small mono">{c.phone}</span>}
              </button>
            </li>
          ))
        )}
      </ul>
    </>
  );
}
